import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import {
  Chat,
  Dashboard,
  People,
  ArrowForward,
  Link,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ChatContent = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleLogin = () => {
    window.open(`https://chat.joglo.ink`, "_blank");
  };

  const features = [
    {
      icon: <Chat fontSize="large" />,
      title: t("Dashboard_chatcontent.features.realTimeChat.title"),
      description: t("Dashboard_chatcontent.features.realTimeChat.description"),
    },
    {
      icon: <Dashboard fontSize="large" />,
      title: t("Dashboard_chatcontent.features.adminDashboard.title"),
      description: t(
        "Dashboard_chatcontent.features.adminDashboard.description"
      ),
    },
    {
      icon: <People fontSize="large" />,
      title: t("Dashboard_chatcontent.features.memberManagement.title"),
      description: t(
        "Dashboard_chatcontent.features.memberManagement.description"
      ),
    },
    {
      icon: <Link fontSize="large" />,
      title: t("Dashboard_chatcontent.features.multiChannel.title"),
      description: t("Dashboard_chatcontent.features.multiChannel.description"),
    },
  ];

  return (
    <Box
      sx={{
        mb: 4,
        backgroundColor: "white",
        borderRadius: 2,
        boxShadow: 1,
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      {/* Hero Section - Modern Layout */}
      <Grid
        container
        spacing={6}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: { xs: 4, md: 2 },
          flexDirection: { xs: "column-reverse", md: "row" },
        }}
      >
        <Grid spacing={2} columns={{ xs: 4, md: 12 }}>
          <Box
            sx={{
              px: { xs: 0, md: 4 },
              textAlign: { xs: "center", md: "left" },
              mt: { xs: 3, md: 0 },
            }}
          >
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
                mb: 3,
                mx: { xs: "auto", md: 0 },
              }}
            >
              <Box
                component="img"
                src="/assets/icon/chat-connect-logo.png"
                alt="Chat Connect Logo"
                sx={{
                  width: { xs: "140px", sm: "160px", md: "220px" },
                  height: "auto",
                  maxWidth: "100%",
                }}
              />
            </Box>

            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 2,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                lineHeight: 1.2,
              }}
            >
              {t("Dashboard_chatcontent.hero.title")}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.125rem" },
                mb: { xs: 3, md: 4 },
                lineHeight: 1.7,
              }}
            >
              {t("Dashboard_chatcontent.hero.description")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Button
                variant="contained"
                onClick={handleLogin}
                size="medium"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "1rem",
                  textTransform: "none",
                  background: "linear-gradient(90deg, #3f51b5, #2196f3)",
                  boxShadow: "none",
                  transition: "all 0.3s ease",
                  minWidth: { xs: "100%", sm: "auto" },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
                  },
                }}
              >
                {t("Dashboard_chatcontent.buttons.getStarted")}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Features Section - Card Grid */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            color: theme.palette.primary.main,
            fontSize: { xs: "1.75rem", md: "2rem" },
          }}
        >
          {t("Dashboard_chatcontent.sections.features")}
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {features.map((feature, index) => (
            <Grid
              spacing={2}
              columns={{ xs: 12, md: 4, sm: 6 }}
              key={index}
              sx={{
                display: "flex",
                justifyContent: index % 2 === 0 ? "flex-end" : "flex-start",
                [theme.breakpoints.down("sm")]: {
                  justifyContent: "center",
                },
              }}
            >
              <Card
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  height: "100%",
                  minHeight: { xs: 220, md: 250 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: { xs: 2, md: 3 },
                  border: "1px solid",
                  borderColor: theme.palette.grey[200],
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 3,
                    borderColor: theme.palette.primary.light,
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    width: 60,
                    height: 60,
                    mb: 3,
                  }}
                >
                  {feature.icon}
                </Avatar>

                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      px: { xs: 0, sm: 1, md: 2 },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Section - Modern Design */}
      <Box
        sx={{
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 6 },
          borderRadius: 3,
          textAlign: "center",
          color: "common.white",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)",
          boxShadow: "0 8px 24px rgba(33, 150, 243, 0.3)",
          mx: { xs: 0, md: "auto" },
          maxWidth: { md: 1000 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            bgcolor: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: "rgba(255, 255, 255, 0.1)",
          }}
        />

        <Box position="relative" zIndex={1}>
          <Typography
            variant="h3"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              lineHeight: 1.2,
            }}
          >
            {t("Dashboard_chatcontent.cta.title")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: { xs: 3, md: 4 },
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
              lineHeight: 1.7,
            }}
          >
            {t("Dashboard_chatcontent.cta.description")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              onClick={handleLogin}
              size="medium"
              endIcon={<ArrowForward />}
              sx={{
                px: { xs: 3, md: 4 },
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "1rem",
                bgcolor: "common.white",
                color: "primary.main",
                minWidth: { xs: "100%", sm: "auto" },
                "&:hover": {
                  bgcolor: "common.white",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {t("Dashboard_chatcontent.buttons.getStarted")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatContent;
